import { format } from "date-fns";
import { round } from "lodash-es";

import type { MaxRole } from "../shared/types/roles";

import { filterMap } from "./collections";
import {
  CSVData,
  CSVExportResultType,
  createSafeFilename,
  formatComment,
  formatDate,
  formatDateISO,
  formatGroupWork,
  formatOutcomes,
  formatPercentage,
  formatPoints,
} from "./file";
import { getClassSessionDurationInMinutes, isStudentPresent } from "./metrics-utils";
import { getUserFullName } from "./user-utils";

import type {
  ExtendedClassSessionSummaryType,
  ExtendedUserEngagementDataType,
} from "api/api-data-types";
import {
  FullAssessmentData,
  PageAssessmentSummaryData,
  SectionAssessmentSummaryData,
  SectionEnrollmentData,
  SectionOutcomeSummaryData,
  StudentOutcomeCompetencyData,
} from "api/api-reporting-types";
import {
  ClassSessionCategory,
  ClassSessionType,
  type ClassSessionUserSummaryType,
  type OrgUserType,
  UserType,
} from "components/server-types";
import { t } from "i18n/i18n";

/** Parses a timestamp as UTC. TinyBird returns "YYYY-MM-DD HH:MM:SS" without timezone. */
export function parseUtcTimestamp(timestamp: string): Date {
  const utcStr = /Z|[+-]\d{2}:?\d{2}$/.test(timestamp)
    ? timestamp
    : `${timestamp.replace(" ", "T")}Z`;
  return new Date(utcStr);
}

export function generateSectionAssessmentSummaryCSV(
  sectionData: SectionAssessmentSummaryData[],
  termTitle: string,
  dateRange: { startDate: string; endDate: string },
): CSVExportResultType {
  const csvData: CSVData = {
    headers: [
      t("assessment_summary_report.columns.course"),
      t("assessment_summary_report.columns.section_title"),
      t("assessment_summary_report.columns.instructors"),
      t("assessment_summary_report.columns.last_assessed"),
      t("assessment_summary_report.columns.percent_assessed"),
      t("assessment_summary_report.columns.percent_with_comments"),
      t("assessment_summary_report.columns.comment_length"),
      t("assessment_summary_report.columns.wait_time"),
      t("assessment_summary_report.columns.assessments_per_student"),
    ],

    rows: sectionData.map((row) => [
      row.course_code,
      row.title,
      row.instructor_names.join(", "),
      formatDate(row.date_last_assessed),
      formatPercentage(row.assessment_percentage),
      formatPercentage(row.comment_percentage),
      row.avg_comment_length,
      row.avg_assessment_delay_days,
      row.avg_student_assessments,
    ]),
  };

  const filename = createSafeFilename("ForumAssessmentSummaryReport", termTitle, dateRange);

  return { csvData, filename };
}

export function generateSectionEnrollmentCSV(
  enrollmentData: SectionEnrollmentData[],
  termTitle: string,
): CSVExportResultType {
  const csvData: CSVData = {
    headers: [
      t("section_enrollment_report.columns.term"),
      t("section_enrollment_report.columns.course"),
      t("section_enrollment_report.columns.section_title"),
      t("section_enrollment_report.columns.role"),
      t("section_enrollment_report.columns.name"),
      t("section_enrollment_report.columns.email"),
      t("section_enrollment_report.columns.disabled_status"),
      t("section_enrollment_report.columns.external_user_id"),
      t("section_enrollment_report.columns.external_section_id"),
      t("section_enrollment_report.columns.invite_status"),
    ],

    rows: enrollmentData.map((row) => [
      row.term,
      row.course_code,
      row.section_title,
      t(`section_enrollment_report.roles.${row.role}`),
      row.name,
      row.email,
      t(`section_enrollment_report.disabled_status_values.${row.disabled_status}`),
      row.external_user_id || "",
      row.external_section_id || "",
      t(`section_enrollment_report.invite_status.${row.invite_status}`),
    ]),
  };

  const filename = `ForumSectionEnrollmentReport-${termTitle.replace(/[^a-zA-Z0-9]/g, "")}-${new Date().toISOString().split("T")[0]}.csv`;

  return { csvData, filename };
}

export function generateAssessmentDetailCSV(
  pageData: PageAssessmentSummaryData[],
  termTitle: string,
  dateRange: { startDate: string; endDate: string },
): CSVExportResultType {
  const csvData: CSVData = {
    headers: [
      t("assessment_summary_report.csv_headers.term"),
      t("assessment_summary_report.csv_headers.section"),
      t("assessment_summary_report.csv_headers.instructors"),
      t("assessment_summary_report.csv_headers.page_type"),
      t("assessment_summary_report.csv_headers.page_title"),
      t("assessment_summary_report.csv_headers.assessments_published_on"),
      t("assessment_summary_report.csv_headers.assessment_percentage"),
      t("assessment_summary_report.csv_headers.students_assessed"),
      t("assessment_summary_report.csv_headers.total_students_to_assess"),
      t("assessment_summary_report.csv_headers.latest_responses_collected"),
      t("assessment_summary_report.csv_headers.first_assessment"),
      t("assessment_summary_report.csv_headers.most_recent_assessment"),
      t("assessment_summary_report.csv_headers.comment_percentage"),
      t("assessment_summary_report.csv_headers.avg_comment_length"),
      t("assessment_summary_report.csv_headers.outcomes_assessed"),
    ],

    rows: pageData.map((row) => [
      row.term,
      row.section_title,
      row.instructors.join(", "),
      t(`assessment_summary_report.page_types.${row.page_type}`),
      row.page_title,
      formatDate(row.assessments_published_on),
      `${row.assessment_percentage}%`,
      row.students_assessed,
      row.total_students_to_assess,
      formatDate(row.latest_responses_collected),
      formatDate(row.first_assessment),
      formatDate(row.most_recent_assessment),
      `${row.comment_percentage}%`,
      row.avg_comment_length,
      formatOutcomes(row.outcomes_assessed),
    ]),
  };

  const filename = createSafeFilename("ForumAssessmentDetailedReport", termTitle, dateRange);

  return { csvData, filename };
}

export function generateFullAssessmentCSV(
  assessmentData: FullAssessmentData[],
  termTitle: string,
  dateRange: { startDate: string; endDate: string },
): CSVExportResultType {
  // Sort by student name (case insensitive, alphabetical)
  const sortedData = [...assessmentData].sort((a, b) =>
    a.student_name.toLowerCase().localeCompare(b.student_name.toLowerCase()),
  );

  const headers = [
    t("full_assessment_report.csv_headers.student_name"),
    t("full_assessment_report.csv_headers.external_id"),
    t("full_assessment_report.csv_headers.student_email"),
    t("full_assessment_report.csv_headers.term"),
    t("full_assessment_report.csv_headers.course"),
    t("full_assessment_report.csv_headers.section_title"),
    t("full_assessment_report.csv_headers.page_type"),
    t("full_assessment_report.csv_headers.page_title"),
    t("full_assessment_report.csv_headers.total_possible_points"),
    t("full_assessment_report.csv_headers.given_points"),
    t("full_assessment_report.csv_headers.group_work"),
    t("full_assessment_report.csv_headers.grader_name"),
    t("full_assessment_report.csv_headers.grader_email"),
    t("full_assessment_report.csv_headers.last_updated_date"),
    t("full_assessment_report.csv_headers.comment"),
  ];

  // Add outcome headers (1-8)
  for (let i = 1; i <= 8; i += 1) {
    headers.push(t("full_assessment_report.outcome_title", { outcome_number: i }));
    headers.push(t("full_assessment_report.outcome_score", { outcome_number: i }));
  }

  const rows = sortedData.map((row) => {
    const baseRow = [
      row.student_name,
      row.external_id,
      row.student_email,
      row.term,
      row.course_code,
      row.section_title,
      row.page_type,
      row.page_title,
      row.total_possible_points.toString(),
      formatPoints(row.given_points),
      formatGroupWork(row.group_work),
      row.grader_name,
      row.grader_email,
      formatDateISO(row.date_assessment_last_updated),
      formatComment(row.comment),
    ];

    // Add outcomes (up to 8)
    for (let i = 0; i < 8; i += 1) {
      const outcome = row.outcomes[i];
      if (outcome) {
        baseRow.push(outcome.outcome_name);
        baseRow.push(outcome.outcome_score.toString());
      } else {
        baseRow.push(""); // Empty outcome name
        baseRow.push(""); // Empty outcome score
      }
    }

    return baseRow;
  });

  const csvData: CSVData = {
    headers,
    rows,
  };

  const filename = createSafeFilename("ForumFullAssessmentReport", termTitle, dateRange);

  return { csvData, filename };
}

export function generateSectionOutcomeSummaryCSV(
  outcomeData: SectionOutcomeSummaryData[],
  termTitle: string,
  outcomeColumns: Array<{ id: string; title: string }>,
): CSVExportResultType {
  // Format outcome titles using localized format
  const outcomeHeaders = outcomeColumns.map((oc) => {
    const formattedTitle = t("common.title_with_average", { title: oc.title });
    return `#${formattedTitle}`;
  });

  // Create headers with localized strings
  const headers = [
    t("student_learning_outcomes_report.columns.student_name"),
    t("student_learning_outcomes_report.columns.student_external_id"),
    t("student_learning_outcomes_report.columns.student_email"),
    t("student_learning_outcomes_report.columns.term"),
    t("student_learning_outcomes_report.columns.course"),
    t("student_learning_outcomes_report.columns.section_title"),
    t("student_learning_outcomes_report.columns.section_external_id"),
    ...outcomeHeaders,
  ];

  // Create rows - one row per student-course combination
  const rows = outcomeData.map((row) => {
    const baseRow = [
      row.student_name,
      row.external_id || "",
      row.email,
      row.term_title,
      row.course_code,
      row.section_title,
      row.section_external_id || "",
    ];

    // Create a map of outcome_id to avg_score for quick lookup
    const outcomesMap = new Map<string, number>();
    for (const outcome of row.outcomes) {
      outcomesMap.set(outcome.outcome_id, outcome.avg_score);
    }

    // Add outcome values for each outcome column
    for (const outcomeColumn of outcomeColumns) {
      const score = outcomesMap.get(outcomeColumn.id);
      baseRow.push(score !== undefined ? round(score, 1).toString() : "");
    }

    return baseRow;
  });

  const csvData: CSVData = {
    headers,
    rows,
  };

  const sanitizedTerm = termTitle.replace(/[^a-zA-Z0-9]/g, "");
  const filename = `ForumStudentLearningOutcomeReportByCourse-${sanitizedTerm}-${new Date().toISOString().split("T")[0]}.csv`;

  return { csvData, filename };
}

export function generateStudentOutcomeCompetencyCSV(
  competencyData: StudentOutcomeCompetencyData[],
  termTitle: string,
  outcomeGroups: Array<{ id: string; title: string }>,
  outcomeSubgroups: Array<{ id: string; title: string }>,
  outcomes: Array<{ id: string; title: string }>,
): CSVExportResultType {
  // Format group, subgroup, and outcome titles using localized format
  const groupHeaders = outcomeGroups.map((og) =>
    t("student_learning_outcomes_report.columns.outcome_group_title", { title: og.title }),
  );
  const subgroupHeaders = outcomeSubgroups.map((os) =>
    t("student_learning_outcomes_report.columns.outcome_subgroup_title", { title: os.title }),
  );
  const outcomeHeaders = outcomes.map((oc) => {
    const formattedTitle = t("common.title_with_average", { title: oc.title });
    return `#${formattedTitle}`;
  });

  // Create headers with localized strings
  const headers = [
    t("student_learning_outcomes_report.columns.student_name"),
    t("student_learning_outcomes_report.columns.student_external_id"),
    t("student_learning_outcomes_report.columns.student_email"),
    t("student_learning_outcomes_report.columns.term"),
    ...groupHeaders,
    ...subgroupHeaders,
    ...outcomeHeaders,
  ];

  // Create rows - one row per student
  const rows = competencyData.map((row) => {
    const baseRow = [row.student_name, row.external_id || "", row.email, termTitle];

    // Create maps for quick lookup
    const groupsMap = new Map<string, number>();
    for (const group of row.outcome_groups) {
      groupsMap.set(group.group_id, group.avg_score);
    }

    const subgroupsMap = new Map<string, number>();
    for (const subgroup of row.outcome_subgroups) {
      subgroupsMap.set(subgroup.subgroup_id, subgroup.avg_score);
    }

    const outcomesMap = new Map<string, number>();
    for (const outcome of row.outcomes) {
      outcomesMap.set(outcome.outcome_id, outcome.avg_score);
    }

    // Add group values
    for (const group of outcomeGroups) {
      const score = groupsMap.get(group.id);
      baseRow.push(score !== undefined ? round(score, 1).toString() : "");
    }

    // Add subgroup values
    for (const subgroup of outcomeSubgroups) {
      const score = subgroupsMap.get(subgroup.id);
      baseRow.push(score !== undefined ? round(score, 1).toString() : "");
    }

    // Add outcome values
    for (const outcome of outcomes) {
      const score = outcomesMap.get(outcome.id);
      baseRow.push(score !== undefined ? round(score, 1).toString() : "");
    }

    return baseRow;
  });

  const csvData: CSVData = {
    headers,
    rows,
  };

  // Handle "All Terms" for filename - check both English and Spanish
  const sanitizedTerm = termTitle === "" ? "AllTime" : termTitle.replace(/[^a-zA-Z0-9]/g, "");
  const filename = `ForumStudentLearningOutcomeReport-${sanitizedTerm}-${new Date().toISOString().split("T")[0]}.csv`;

  return { csvData, filename };
}

export function generateSessionLogCSV(
  sessions: ExtendedClassSessionSummaryType[],
  sectionTitle: string,
  hasVirtualSessions: boolean,
): CSVExportResultType {
  const headers = [
    t("session_log_view.columns.started_at.label"),
    t("session_log_view.columns.duration.label"),
    t("session_log_view.columns.class_type.label"),
    t("session_log_view.columns.attendance.label"),
    t("session_log_view.columns.reactions.label"),
    t("session_log_view.columns.hand_raises.label"),
    t("session_log_view.columns.chat_messages.label"),
  ];

  if (hasVirtualSessions) {
    headers.push(
      t("session_log_view.columns.student_talk_time.label"),
      t("session_log_view.columns.instructor_talk_time.label"),
    );
  }

  const rows = sessions.map((session) => {
    const duration = getClassSessionDurationInMinutes(session);
    const classType =
      session.category === ClassSessionCategory.VIRTUAL ? "virtual-class" : "in-person-class";

    const row = [
      formatDateISO(session.started_at),
      duration,
      classType,
      Math.round(session.attendance_percentage),
      session.reaction_count ?? 0,
      session.hand_raise_count ?? 0,
      session.chat_message_count ?? 0,
    ];

    if (hasVirtualSessions) {
      // Talk times in minutes
      const studentTalkTime = Math.round((session.student_talk_time_seconds ?? 0) / 60);
      const instructorTalkTime = Math.round((session.instructor_talk_time_seconds ?? 0) / 60);

      row.push(studentTalkTime, instructorTalkTime);
    }

    return row;
  });

  const csvData: CSVData = {
    headers,
    rows,
  };

  const sanitizedSectionTitle = sectionTitle.replace(/[^a-zA-Z0-9]/g, "");
  const filename = `ForumSessionLog-${sanitizedSectionTitle}-${new Date().toISOString().split("T")[0]}.csv`;

  return { csvData, filename };
}

export function generateEngagementDataCSV(
  engagementData: ExtendedUserEngagementDataType[],
  sectionTitle: string,
): CSVExportResultType {
  const headers = [
    t("engagement_table.columns.student.label"),
    t("engagement_table.columns.effort.label"),
    t("engagement_table.columns.performance.label"),
    t("engagement_table.columns.attendance.label"),
    t("engagement_table.columns.talk_time.label"),
    t("engagement_table.columns.risk_level.label"),
  ];
  const rows = engagementData.map((row) => {
    return [
      getUserFullName(row),
      row.effort_level,
      row.performance_level,
      row.attendance_count ?? null,
      row.avg_talk_time_minutes ?? null,
      row.risk_level,
    ];
  });
  const csvData: CSVData = {
    headers,
    rows,
  };
  const sanitizedSectionTitle = sectionTitle.replace(/[^a-zA-Z0-9]/g, "");
  const filename = `ForumEngagementData-${sanitizedSectionTitle}-${new Date().toISOString().split("T")[0]}.csv`;
  return { csvData, filename };
}

export type AttendanceReportRow = {
  studentName: string;
  studentExternalId: string | null;
  studentEmail: string;
  classSessionDate: string;
  classSessionDateTimestamp: number; // Only used for chronological sorting
  timeFirstEntered: string | null;
  minutesInClass: number | null;
  timeLastLeft: string | null;
  sessionStatus: "Present" | "Absent";
  section: string;
};

export function generateAttendanceReportCSV(
  attendanceData: AttendanceReportRow[],
  sectionTitle: string,
): CSVExportResultType {
  const headers = [
    "Student Name",
    "Student External Id",
    "Student Email",
    "Class Session Date",
    "Time First Entered",
    "Minutes in Class",
    "Time Last Left",
    "Session Status",
    "Course",
  ];

  const rows = attendanceData.map((row) => [
    row.studentName,
    row.studentExternalId || "",
    row.studentEmail,
    row.classSessionDate,
    row.timeFirstEntered || "",
    row.minutesInClass ?? "",
    row.timeLastLeft || "",
    row.sessionStatus,
    row.section,
  ]);

  const csvData: CSVData = {
    headers,
    rows,
  };

  const sanitizedSectionTitle = sectionTitle.replace(/[^a-zA-Z0-9]/g, "");
  const filename = `ForumAttendanceReport-${sanitizedSectionTitle}-${new Date().toISOString().split("T")[0]}.csv`;

  return { csvData, filename };
}

const getAttendanceKey = (userId: string, classSessionId: string): string =>
  `${userId}:${classSessionId}`;

export function generateAttendanceReportCSVFromSessions(
  sessions: Pick<ClassSessionType, "id" | "started_at">[],
  users: Record<string, UserType>,
  orgUsersMap: Record<string, OrgUserType>,
  sectionTitle: string,
  studentSummaries: ClassSessionUserSummaryType[],
): CSVExportResultType {
  // Create a map of (user_id, session_id) to attendance metrics
  const summaryMap = new Map<string, ClassSessionUserSummaryType>();
  for (const summary of studentSummaries) {
    const key = getAttendanceKey(summary.user_id, summary.class_session_id);
    summaryMap.set(key, summary);
  }

  // Generate attendance report rows
  const reportRows: AttendanceReportRow[] = [];
  for (const session of sessions) {
    if (!session.started_at) continue; // eslint-disable-line no-continue
    const studentUserIds = filterMap(
      studentSummaries,
      (summary) => summary.class_session_id === session.id,
      (summary) => summary.user_id,
    );

    // Get all students enrolled in the course
    for (const userId of studentUserIds) {
      const user = users[userId];
      if (user) {
        const attendanceKey = getAttendanceKey(userId, session.id);
        const summary = summaryMap.get(attendanceKey);
        const presenceMinutes = summary?.presence_minutes ?? null;
        const sessionStatus = isStudentPresent(presenceMinutes) ? "Present" : "Absent";

        const classSessionDate = format(new Date(session.started_at), "MM-dd-yyyy");
        const classSessionDateTimestamp = new Date(session.started_at).getTime();
        const timeFirstEntered = summary?.first_seen
          ? format(parseUtcTimestamp(summary.first_seen), "HH:mm:ss")
          : null;
        const timeLastLeft = summary?.last_seen
          ? format(parseUtcTimestamp(summary.last_seen), "HH:mm:ss")
          : null;

        reportRows.push({
          studentName: getUserFullName(user),
          studentExternalId: orgUsersMap[userId]?.external_user_id ?? null,
          studentEmail: user.email,
          classSessionDate,
          classSessionDateTimestamp,
          timeFirstEntered,
          minutesInClass: presenceMinutes,
          timeLastLeft,
          sessionStatus,
          section: sectionTitle || "Course",
        });
      }
    }
  }

  // Sort by student name, then by session date (chronologically)
  reportRows.sort((a, b) => {
    const nameCompare = a.studentName.localeCompare(b.studentName);
    if (nameCompare !== 0) return nameCompare;
    return a.classSessionDateTimestamp - b.classSessionDateTimestamp;
  });

  return generateAttendanceReportCSV(reportRows, sectionTitle || "Course");
}

export type OrgUserCSVRow = {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  maxRole: MaxRole;
  external_user_id: string | null;
  invite_status: string;
};

export function generateOrgUsersCSV(
  users: OrgUserCSVRow[],
  orgName: string,
  includeExternalId: boolean,
): CSVExportResultType {
  const headers = [
    t("user_options_menu.table_header_name"),
    t("user_options_menu.table_header_email"),
    t("user_options_menu.table_header_max_role"),
    ...(includeExternalId ? [t("user_options_menu.table_header_external_id")] : []),
    t("user_options_menu.table_header_invite_status"),
  ];

  const rows: (string | number)[][] = users.map((user) => [
    getUserFullName({
      first_name: user.first_name ?? "",
      last_name: user.last_name ?? "",
      email: user.email ?? "",
    }),
    user.email ?? "",
    (t as (key: string) => string)(`user_options_menu.max_role.${user.maxRole}`),
    ...(includeExternalId ? [user.external_user_id ?? ""] : []),
    (t as (key: string) => string)(`invite_status.${user.invite_status}`),
  ]);

  const safeOrgName = orgName.replace(/[^a-zA-Z0-9]/g, "");
  const filename = `ForumOrgUsers-${safeOrgName}-${new Date().toISOString().split("T")[0]}.csv`;

  return { csvData: { headers, rows }, filename };
}
